import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  ADS_CAMPAIGNS_STATISTICS_SLIDE_MENU_METRICS,
  LOADING_CONFIGURATION_METRICS,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import MetaCampaignRow from "./Meta/MetaCampaignRow";
import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../services/adsCampaigns";
import { GOOGLE, META, TIKTOK, LINKEDIN } from "../../../../utils/constants";
import TableContainer from "../../../../components/Tables/TableContainer";
import TableCell from "../../../BillingAndPayments/components/TableCell";
import GoogleCampaignRow from "./Google/GoogleCampaignRow";
import TiktokCampaignRow from "./Tiktok/TiktokCampaignRow";
import LinkedInCampaignRow from "./LinkedIn/LinkedInCampaignRow";
import { useState, useEffect } from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { TABLE_COLUMNS_CONFIG } from "./tableColumnsConfig";
import MetricsVisibilitySelector from "../../../../components/Common/MetricsVisibilitySelector";
import {
  MULTIPLE_CONVERSION_PREFIX,
  getTranslatedGroupName,
} from "../../../../services/google";
import Stack from "@mui/material/Stack";
import { Avatar } from "@mui/material";
import { logoPlatform } from "../../../../utils/string";

const COMPONENT_ROWS = {
  [META]: MetaCampaignRow,
  [GOOGLE]: GoogleCampaignRow,
  [TIKTOK]: TiktokCampaignRow,
  [LINKEDIN]: LinkedInCampaignRow,
};

const TableStatistics = () => {
  const { t } = useTranslationApp();
  const [sortConfigs, setSortConfigs] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({
    [GOOGLE]: {},
    [META]: {},
    [TIKTOK]: {},
    [LINKEDIN]: {},
  });
  const [metricsInitialized, setMetricsInitialized] = useState({
    [GOOGLE]: false,
    [META]: false,
    [TIKTOK]: false,
    [LINKEDIN]: false,
  });

  const { campaign } = useFormContext();
  const { platforms, statistics } = getFromStatisticsAdsCampaign(campaign);

  // Initialize default visibility only once, before backend config loads
  useEffect(() => {
    const initial = {};
    [GOOGLE, META, TIKTOK, LINKEDIN].forEach((platform) => {
      const cols = TABLE_COLUMNS_CONFIG[platform] || [];
      // Only initialize if platform visibility is empty (not loaded from backend yet)
      if (
        !columnVisibility[platform] ||
        Object.keys(columnVisibility[platform]).length === 0
      ) {
        initial[platform] = {};
        cols.forEach((col) => {
          if (col.key !== "platform") {
            if (col.key === "cpc" || col.key === "cpm" || col.key === "reach") {
              initial[platform][col.key] = false;
            } else {
              initial[platform][col.key] = true;
            }
          }
        });
      } else {
        initial[platform] = columnVisibility[platform];
      }
    });
    // Only set if we have platforms to initialize
    const needsInitialization = [GOOGLE, META, TIKTOK, LINKEDIN].some(
      (platform) =>
        !columnVisibility[platform] ||
        Object.keys(columnVisibility[platform]).length === 0
    );
    if (needsInitialization) {
      setColumnVisibility(initial);
    }
    // eslint-disable-next-line
  }, []);

  const handleSort = (platform, key) => {
    setSortConfigs((prevConfigs) => ({
      ...prevConfigs,
      [platform]: {
        key,
        direction:
          prevConfigs[platform]?.key === key &&
          prevConfigs[platform]?.direction === "asc"
            ? "desc"
            : "asc",
      },
    }));
  };

  const SortIcon = ({ platform, columnKey }) => {
    if (sortConfigs[platform]?.key !== columnKey) return null;
    return sortConfigs[platform]?.direction === "asc" ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  const renderTableForPlatform = (platform) => {
    const Component = COMPONENT_ROWS[platform];
    if (!Component) return null;
    const columns = TABLE_COLUMNS_CONFIG[platform] || [];
    const sortConfig = sortConfigs[platform] || { key: null, direction: null };
    const stats = statistics[platform]?.campaign
      ? [statistics[platform].campaign]
      : [];
    const sortedStats = [...stats].sort((a, b) => {
      if (!sortConfig.key) return 0;
      const key = sortConfig.key;
      const comparison = (a[key] || 0) - (b[key] || 0);
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
    const visibility = columnVisibility[platform] || {};
    const isInitialized = metricsInitialized[platform];

    const baseAvailableColumns = columns.filter((col) => {
      if (col.key === "platform") return true;
      const hasData = stats.some((stat) => {
        const value = stat[col.key];
        return (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0)
        );
      });
      return hasData;
    });

    // GOOGLE ONLY: Add dynamic multipleConversion columns (aggregated by category-origin)
    const dynamicMultipleConversionColumns =
      platform === GOOGLE && stats.length > 0
        ? Object.keys(stats[0] || {})
            .filter((k) => k.startsWith(MULTIPLE_CONVERSION_PREFIX))
            .map((key) => ({ key, label: key, sortable: false }))
        : [];

    const availableColumns = [
      ...baseAvailableColumns,
      ...dynamicMultipleConversionColumns,
    ];

    const availableMetrics = availableColumns
      .filter((col) => col.key !== "platform")
      .map((col) => col.key);

    const getMetricLabel = (metricKey) => {
      if (metricKey.startsWith(MULTIPLE_CONVERSION_PREFIX)) {
        const groupKey = metricKey.slice(MULTIPLE_CONVERSION_PREFIX.length);
        if (groupKey.includes("-")) {
          return getTranslatedGroupName(groupKey, t);
        }
        return groupKey;
      }
      const column = availableColumns.find((col) => col.key === metricKey);
      return column ? t(column.label) : metricKey;
    };

    return (
      <Box
        key={platform}
        data-pdf-capture-id={`metrics-table-card-${platform}`}
        sx={{ mb: 3 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Avatar
              data-hide-in-pdf-capture
              src={logoPlatform(platform)}
              variant="square"
              sx={{
                width: 20,
                height: 20,
                ".MuiAvatar-img": { objectFit: "contain" },
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t(platform)}
            </Typography>
          </Box>
          <MetricsVisibilitySelector
            availableMetrics={availableMetrics}
            metricsVisibility={visibility}
            setMetricsVisibility={(newVisibility) => {
              setColumnVisibility((prev) => ({
                ...prev,
                [platform]: newVisibility,
              }));
            }}
            getLabel={getMetricLabel}
            isFromStatisticsLevel={true}
            campaignID={campaign?.id}
            platform={platform}
            onInitializationComplete={() => {
              setMetricsInitialized((prev) => ({
                ...prev,
                [platform]: true,
              }));
            }}
          />
        </Stack>
        {!isInitialized ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 100,
              color: "text.secondary",
            }}
          >
            <Typography>{t(LOADING_CONFIGURATION_METRICS)}</Typography>
          </Box>
        ) : (
          <TableContainer
            hasPagination={false}
            hasMarginBottom={false}
            keys={1}
          >
            <TableHead>
              <TableRow sx={{ padding: 0 }}>
                <TableCell isHeader={true} sx={{ width: 25 }} />
                {availableColumns.find((col) => col.key === "platform") && (
                  <TableCell key="platform" isHeader={true}>
                    {t(
                      availableColumns.find((col) => col.key === "platform")
                        .label
                    )}
                  </TableCell>
                )}
                {availableColumns
                  .filter(
                    (col) =>
                      col.key !== "platform" && visibility[col.key] !== false
                  )
                  .map((col) => (
                    <TableCell
                      key={col.key}
                      isHeader={true}
                      sx={{
                        width: 192,
                        maxWidth: 192,
                      }}
                    >
                      {col.sortable ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: 1,
                            cursor: "pointer",
                            textWrap: "auto",
                          }}
                          onClick={() => handleSort(platform, col.key)}
                        >
                          {getMetricLabel(col.key)}
                          <SortIcon platform={platform} columnKey={col.key} />
                        </Box>
                      ) : (
                        getMetricLabel(col.key)
                      )}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStats.map((stat, index) => (
                <Component
                  key={platform}
                  index={index}
                  platformData={campaign?.platformsData?.[platform] || {}}
                  sortConfig={sortConfig}
                  columns={[
                    ...(availableColumns.find((col) => col.key === "platform")
                      ? [availableColumns.find((col) => col.key === "platform")]
                      : []),
                    ...availableColumns.filter(
                      (col) =>
                        col.key !== "platform" && visibility[col.key] !== false
                    ),
                  ]}
                  stat={stat}
                />
              ))}
            </TableBody>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <Box
      id="metrics-section"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        pt: 3,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        {t(ADS_CAMPAIGNS_STATISTICS_SLIDE_MENU_METRICS)}
      </Typography>
      {platforms.map((platform) => renderTableForPlatform(platform))}
    </Box>
  );
};

export default TableStatistics;
